import React from 'react';
import { Card, CardContent, Typography, Box, IconButton } from '@mui/material';
import { ITalentReview } from '../../../types/global/helper';
import { ThspianAvatar } from '../../Avatar/ThspianAvatar';
import { StarRating } from '../../EsVideoVotingModal/EsRatingStars';
import { useThemeContext } from '../../../hook/useThemeManager';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import { theme } from '../../../constants/theme';
import { QuoteIcon } from '../../CustomSVGIcons/QuoteIcon';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';

export interface IReviewCardProps {
    review: ITalentReview;
    canEditDelete?: boolean;
    onEdit?: () => void;
    onDelete?: () => void;
    onLike?: () => void;
    onDislike?: () => void;
    onDeleteLike?: (likeId: string) => void;
    likeLoading?: boolean;
    dislikeLoading?: boolean;
}

export const ReviewCard: React.FC<IReviewCardProps> = ({
    review,
    canEditDelete,
    onEdit,
    onDelete,
    onLike,
    onDislike,
    onDeleteLike,
    likeLoading,
    dislikeLoading,
}) => {
    const { themeStyle } = useThemeContext();
    const {
        rating,
        review_text,
        review_subject,
        user_has_liked,
        user_has_disliked,
        total_likes,
        total_dislikes,
        like_object,
        dislike_object,
        user,
        edited,
    } = review;
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const handleMenuClose = () => {
        setAnchorEl(null);
    };
    const handleEdit = () => {
        handleMenuClose();
        if (onEdit) onEdit();
    };
    const handleDelete = () => {
        handleMenuClose();
        if (onDelete) onDelete();
    };
    return (
        <Card
            sx={{
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                minHeight: 180,
                p: 2,
                borderRadius: 3,
                backgroundColor: theme[themeStyle].deep_grey_lighter,
                position: 'relative',
                border: `1.5px solid ${theme[themeStyle].deep_grey}`,
                boxShadow: 4,
                transition: 'box-shadow 0.2s, border 0.2s',
                '&:hover': {
                    boxShadow: 8,
                    border: `1.5px solid ${theme[themeStyle].highlight}`,
                },
                overflow: 'visible',
            }}>
            {/* Decorative Quote Icon */}
            <Box sx={{ position: 'absolute', right: 12, top: 8, opacity: 0.12, zIndex: 0 }}>
                <QuoteIcon fill={theme[themeStyle].highlight} sx={{ fontSize: 80 }} />
            </Box>
            {/* Reviewer Info */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, zIndex: 1 }}>
                <ThspianAvatar user={user} />
                <Box>
                    <Typography
                        sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 14, color: theme[themeStyle].tag_white }}>
                        {user.firstName} {user.lastName}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography
                            sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 13, color: theme[themeStyle].highlight }}>
                            {rating}
                        </Typography>
                        <StarRating
                            style={{ width: 'fit-content', height: 'fit-content', paddingLeft: 0, fontSize: 14 }}
                            disabled
                            ratingProps={{
                                value: rating ?? 0,
                                readOnly: true,
                                sx: {
                                    fontSize: 18,
                                    color: theme[themeStyle].highlight,
                                },
                            }}
                        />
                    </Box>
                </Box>
                {canEditDelete && (
                    <>
                        <IconButton
                            size="small"
                            onClick={handleMenuOpen}
                            sx={{ color: theme[themeStyle].highlight, ml: 'auto' }}>
                            <MoreVertIcon />
                        </IconButton>
                        <Menu
                            anchorEl={anchorEl}
                            open={open}
                            onClose={handleMenuClose}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
                            <MenuItem onClick={handleEdit}>Edit</MenuItem>
                            <MenuItem onClick={handleDelete} sx={{ color: theme[themeStyle].secondary_red }}>
                                Delete
                            </MenuItem>
                        </Menu>
                    </>
                )}
            </Box>
            {/* Review Content */}
            <CardContent sx={{ p: 0, mt: 2, zIndex: 1 }}>
                <Typography
                    sx={{
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        fontSize: 13,
                        color: theme[themeStyle].highlight,
                        mb: 0.5,
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                    }}>
                    {review_subject}
                </Typography>
                <Typography
                    component={'span'}
                    sx={{
                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                        fontSize: 12,
                        color: theme[themeStyle].tag_white,
                        opacity: 0.92,
                        lineHeight: 1.5,
                        minHeight: 40,
                    }}>
                    {review_text}{' '}
                    <Typography
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            fontSize: 8,
                            color: theme[themeStyle].tag_white,
                            opacity: 0.7,
                            lineHeight: 1.5,
                            minHeight: 40,
                        }}>
                        {edited && ' (edited)'}
                    </Typography>
                </Typography>
            </CardContent>
            {/* Like/Dislike Buttons */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    gap: 1,
                    mt: 'auto',
                    zIndex: 1,
                }}>
                <IconButton
                    size="small"
                    sx={{ color: user_has_liked ? theme[themeStyle].main_blue : theme[themeStyle].tag_white }}
                    onClick={() =>
                        user_has_liked
                            ? onDeleteLike && like_object && onDeleteLike(like_object[0]._id)
                            : onLike && onLike()
                    }
                    disabled={user_has_disliked || likeLoading}>
                    <ThumbUpIcon fontSize="small" />
                </IconButton>
                <Typography sx={{ fontSize: 13, color: theme[themeStyle].highlight, mr: 1 }}>
                    {total_likes ?? 0}
                </Typography>
                <IconButton
                    size="small"
                    sx={{ color: user_has_disliked ? theme[themeStyle].primary_red : theme[themeStyle].tag_white }}
                    onClick={() =>
                        user_has_disliked
                            ? dislike_object && onDeleteLike && onDeleteLike(dislike_object[0]._id)
                            : onDislike && onDislike()
                    }
                    disabled={user_has_liked || dislikeLoading}>
                    <ThumbDownIcon fontSize="small" />
                </IconButton>
                <Typography sx={{ fontSize: 13, color: theme[themeStyle].highlight }}>{total_dislikes ?? 0}</Typography>
            </Box>
        </Card>
    );
};
